import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  X,
  Calendar,
  CalendarDays,
  CalendarClock,
  List,
  MapPin,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Navigation,
  ArrowRightLeft,
  Building2,
  SlidersHorizontal,
  Clock,
  ChevronDown,
  Route,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { autoScheduleAPI, clientsAPI, schedulingSettingsAPI, usersAPI } from '@shared/apiClient';
import TechRouteMapModal from './TechRouteMapModal';

const REASON_LABELS = {
  needs_geocoding: 'Needs geocoding',
  no_capacity_or_slot: 'No capacity or slot',
  no_capable_technician: 'No capable technician',
  no_cooling_capable_technician: 'No tech for this cooling type',
  no_allowed_technician: 'Blocked by technician rules',
  no_technicians_enabled: 'No technicians enabled',
  no_work_days: 'No work days in range',
};

const STRATEGY_OPTIONS = [
  { id: 'minimize_travel', label: 'Minimize travel time' },
  { id: 'maximize_visits', label: 'Maximize visits per day' },
  { id: 'farthest_first', label: 'Farthest from shop first' },
  { id: 'closest_first', label: 'Closest to shop first' },
  { id: 'balanced_cluster', label: 'Balanced geographic clusters' },
];

// Distinct, high-contrast palette used to color technicians across the calendar.
const TECH_PALETTE = [
  '#2563eb', // blue
  '#059669', // emerald
  '#d97706', // amber
  '#7c3aed', // violet
  '#db2777', // pink
  '#0d9488', // teal
  '#dc2626', // red
  '#4f46e5', // indigo
  '#65a30d', // lime
  '#ea580c', // orange
  '#0891b2', // cyan
  '#9333ea', // purple
];

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Compact "1h 30m" / "45m" formatting for minute values.
const fmtMin = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
};

const formatDefaultEndDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 13);
  return d.toISOString().split('T')[0];
};

const formatDefaultStartDate = () => new Date().toISOString().split('T')[0];

const parseLocalDate = (str) => {
  const [y, m, d] = String(str).split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDayLabel = (str) => {
  const date = parseLocalDate(str);
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
};

function buildCalendarWeeks(startStr, endStr) {
  const start = parseLocalDate(startStr);
  const end = parseLocalDate(endStr);
  if (end < start) return [];

  const gridStart = new Date(start);
  gridStart.setDate(start.getDate() - start.getDay());
  const gridEnd = new Date(end);
  gridEnd.setDate(end.getDate() + (6 - end.getDay()));

  const weeks = [];
  const cursor = new Date(gridStart);
  while (cursor <= gridEnd) {
    const week = [];
    for (let i = 0; i < 7; i += 1) {
      week.push(formatLocalDate(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

function buildSelectedKeys(days) {
  const keys = new Set();
  days.forEach((day) => {
    day.techSlots.forEach((slot) => {
      slot.assignments.forEach((unit) => {
        unit.members.forEach((m) => keys.add(m.serviceCallId));
      });
    });
  });
  return keys;
}

function flattenAssignments(days, selectedKeys) {
  const assignments = [];
  days.forEach((day) => {
    day.techSlots.forEach((slot) => {
      slot.assignments.forEach((unit) => {
        unit.members.forEach((member) => {
          if (selectedKeys.has(member.serviceCallId)) {
            assignments.push({
              serviceCallId: member.serviceCallId,
              technicianId: slot.technicianId,
              scheduledDate: slot.date,
              typeId: member.typeId,
            });
          }
        });
      });
    });
  });
  return assignments;
}

// Collect every serviceCallId within a slot (used for slot-level select toggles).
function slotMemberKeys(slot) {
  const keys = [];
  slot.assignments.forEach((unit) => {
    unit.members.forEach((m) => keys.push(m.serviceCallId));
  });
  return keys;
}

// Same as slotMemberKeys but omits one unit -- used to compute what's left
// behind in a slot after moving that unit elsewhere.
function slotMemberKeysExcludingUnit(slot, unitKey) {
  const keys = [];
  slot.assignments.forEach((unit) => {
    if (unit.unitKey === unitKey) return;
    unit.members.forEach((m) => keys.push(m.serviceCallId));
  });
  return keys;
}

// Merge freshly recomputed slot(s) back into the days array, creating or
// removing days/slots as needed so an empty slot disappears and a slot on a
// brand-new date gets its own day entry.
function applySlotUpdates(days, updates) {
  let next = days.map((d) => ({ ...d, techSlots: [...d.techSlots] }));
  updates.forEach(({ date, technicianId, slot }) => {
    const dayIdx = next.findIndex((d) => d.date === date);
    if (!slot || slot.assignments.length === 0) {
      if (dayIdx !== -1) {
        next[dayIdx].techSlots = next[dayIdx].techSlots.filter((s) => s.technicianId !== technicianId);
        if (next[dayIdx].techSlots.length === 0) {
          next = next.filter((d) => d.date !== date);
        }
      }
      return;
    }
    if (dayIdx === -1) {
      next.push({ date, techSlots: [slot] });
      next.sort((a, b) => a.date.localeCompare(b.date));
      return;
    }
    const slotIdx = next[dayIdx].techSlots.findIndex((s) => s.technicianId === technicianId);
    if (slotIdx === -1) {
      next[dayIdx].techSlots.push(slot);
    } else {
      next[dayIdx].techSlots[slotIdx] = slot;
    }
  });
  return next;
}

export default function AutoScheduleModal({ isOpen, onClose }) {
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState(formatDefaultStartDate);
  const [endDate, setEndDate] = useState(formatDefaultEndDate);
  const [preview, setPreview] = useState(null);
  const [days, setDays] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [viewMode, setViewMode] = useState('calendar');
  const [techFilter, setTechFilter] = useState(null); // null = all technicians
  const [activeDate, setActiveDate] = useState(null);
  // { slotKey, unitKey, fromDate, fromTechnicianId, technicianId, date }
  const [moveForm, setMoveForm] = useState(null);
  const [moving, setMoving] = useState(false);
  const [routeSlot, setRouteSlot] = useState(null);

  // --- Advanced options ---------------------------------------------------
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedTechIds, setSelectedTechIds] = useState(null); // null until initialized from roster
  const [prioritizeOverdue, setPrioritizeOverdue] = useState(false);
  const [prioritizeThisMonth, setPrioritizeThisMonth] = useState(false);
  const [callKindFilter, setCallKindFilter] = useState('all');
  const [maxStopsPerTech, setMaxStopsPerTech] = useState('');
  const [includeScheduled, setIncludeScheduled] = useState(false);
  const [strategy, setStrategy] = useState('minimize_travel');
  const [compareStrategyIds, setCompareStrategyIds] = useState(new Set(['minimize_travel', 'maximize_visits', 'farthest_first']));
  const [compareResults, setCompareResults] = useState(null);
  const advancedRef = useRef(null);

  const { data: geocodeData } = useQuery(
    'geocodingFailures',
    () => clientsAPI.getGeocodingFailures(),
    { enabled: isOpen }
  );

  const { data: techData } = useQuery(
    ['users', 'technicians', 'autoScheduleModal'],
    () => usersAPI.getUsers({ role: 'technician', limit: 100 }),
    { enabled: isOpen }
  );

  const { data: schedulingSettingsData } = useQuery(
    'schedulingSettings',
    () => schedulingSettingsAPI.get(),
    { enabled: isOpen }
  );

  const geocodeCount = geocodeData?.data?.clients?.length || 0;
  const allTechnicians = useMemo(
    () => (techData?.data?.users || []).filter((t) => t.isActive && !t.isDeleted),
    [techData]
  );

  // Default the tech picker to whoever has "Include in Auto-Schedule" on,
  // mirroring the server default -- but only once, so the admin's manual
  // adjustments aren't clobbered by refetches.
  useEffect(() => {
    if (selectedTechIds !== null || allTechnicians.length === 0) return;
    setSelectedTechIds(new Set(allTechnicians.filter((t) => t.includeInAutoSchedule).map((t) => t.id)));
  }, [allTechnicians, selectedTechIds]);

  useEffect(() => {
    const defaultStrategy = schedulingSettingsData?.data?.settings?.defaultStrategy;
    if (defaultStrategy && STRATEGY_OPTIONS.some((o) => o.id === defaultStrategy)) {
      setStrategy(defaultStrategy);
    }
  }, [schedulingSettingsData]);

  const allTechIdsSelected = useMemo(
    () => selectedTechIds && allTechnicians.length > 0 && allTechnicians.every((t) => selectedTechIds.has(t.id)),
    [selectedTechIds, allTechnicians]
  );

  const toggleTechSelection = (techId) => {
    setSelectedTechIds((prev) => {
      const next = new Set(prev || []);
      if (next.has(techId)) next.delete(techId);
      else next.add(techId);
      return next;
    });
  };

  const advancedFilterCount = [
    selectedTechIds && !allTechIdsSelected,
    prioritizeOverdue,
    prioritizeThisMonth,
    callKindFilter !== 'all',
    Boolean(maxStopsPerTech),
  ].filter(Boolean).length;

  const buildPreviewPayload = () => ({
    startDate,
    endDate,
    technicianIds: selectedTechIds ? [...selectedTechIds] : undefined,
    prioritizeOverdue,
    prioritizeThisMonth,
    callKindFilter,
    maxStopsPerTech: maxStopsPerTech ? Number(maxStopsPerTech) : null,
    includeScheduled,
  });

  const loadPreview = (p) => {
    setPreview(p);
    setDays(p.days);
    setSelectedKeys(buildSelectedKeys(p.days));
    setTechFilter(null);
    setActiveDate(p.days[0]?.date || null);
    setMoveForm(null);
    setCompareResults(null);
  };

  const previewMutation = useMutation(
    () => autoScheduleAPI.preview({
      ...buildPreviewPayload(),
      strategy,
    }),
    {
      onSuccess: (response) => {
        loadPreview(response.data.preview);
        toast.success('Preview generated');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Preview failed');
      },
    }
  );

  const compareMutation = useMutation(
    () => autoScheduleAPI.previewCompare({
      ...buildPreviewPayload(),
      strategies: [...compareStrategyIds],
    }),
    {
      onSuccess: (response) => {
        setCompareResults(response.data.comparison);
        setPreview(null);
        setDays([]);
        setSelectedKeys(new Set());
        toast.success('Strategy comparison ready');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Compare failed');
      },
    }
  );

  const toggleCompareStrategy = (strategyId) => {
    setCompareStrategyIds((prev) => {
      const next = new Set(prev);
      if (next.has(strategyId)) {
        if (next.size <= 2) return prev;
        next.delete(strategyId);
      } else if (next.size < 4) {
        next.add(strategyId);
      }
      return next;
    });
  };

  const applyMutation = useMutation(
    (assignments) => autoScheduleAPI.apply({ assignments }),
    {
      onSuccess: (response) => {
        const { applied, failed } = response.data;
        toast.success(`Scheduled ${applied} visit(s)${failed ? `, ${failed} failed` : ''}`);
        queryClient.invalidateQueries('schedulingBacklog');
        queryClient.invalidateQueries('calendarServiceCalls');
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('mapPoints');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Apply failed');
      },
    }
  );

  // Assign a stable color to each technician, keyed off the full active
  // roster (sorted by name) so colors don't shuffle as visits get moved
  // around and so a tech with zero original stops still gets one.
  const techColorMap = useMemo(() => {
    const map = {};
    const sorted = [...allTechnicians].sort((a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
    );
    sorted.forEach((t, idx) => {
      map[t.id] = TECH_PALETTE[idx % TECH_PALETTE.length];
    });
    // Fallback for any technician not present in the roster query.
    let fallbackIdx = sorted.length;
    days.forEach((day) => {
      day.techSlots.forEach((slot) => {
        if (!map[slot.technicianId]) {
          map[slot.technicianId] = TECH_PALETTE[fallbackIdx % TECH_PALETTE.length];
          fallbackIdx += 1;
        }
      });
    });
    return map;
  }, [allTechnicians, days]);

  // Filter chips: only technicians currently holding at least one visit.
  const technicians = useMemo(() => {
    const seen = new Map();
    days.forEach((day) => {
      day.techSlots.forEach((slot) => {
        if (!seen.has(slot.technicianId)) {
          seen.set(slot.technicianId, {
            id: slot.technicianId,
            name: slot.technicianName,
            color: techColorMap[slot.technicianId],
          });
        }
      });
    });
    return [...seen.values()];
  }, [days, techColorMap]);

  // Every active technician, for the "reassign to" picker (includes techs
  // with zero visits so far).
  const reassignableTechnicians = useMemo(() => {
    return [...allTechnicians]
      .map((t) => ({ id: t.id, name: `${t.firstName} ${t.lastName}`, color: techColorMap[t.id] }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allTechnicians, techColorMap]);

  const daysByDate = useMemo(() => {
    const map = {};
    days.forEach((day) => { map[day.date] = day; });
    return map;
  }, [days]);

  const calendarWeeks = useMemo(() => {
    if (!preview) return [];
    const allDates = days.map((d) => d.date);
    const minDate = allDates.length ? [preview.startDate, ...allDates].sort()[0] : preview.startDate;
    const maxDate = allDates.length ? [preview.endDate, ...allDates].sort().slice(-1)[0] : preview.endDate;
    return buildCalendarWeeks(minDate, maxDate);
  }, [preview, days]);

  const visibleSlots = (day) => {
    if (!day) return [];
    if (!techFilter) return day.techSlots;
    return day.techSlots.filter((s) => s.technicianId === techFilter);
  };

  const toggleMember = (serviceCallId) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(serviceCallId)) next.delete(serviceCallId);
      else next.add(serviceCallId);
      return next;
    });
  };

  const toggleKeys = (keys) => {
    const allSelected = keys.every((k) => selectedKeys.has(k));
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => {
        if (allSelected) next.delete(k);
        else next.add(k);
      });
      return next;
    });
  };

  const toggleSlot = (slot) => toggleKeys(slotMemberKeys(slot));

  const toggleDay = (day) => {
    const keys = [];
    visibleSlots(day).forEach((slot) => {
      slotMemberKeys(slot).forEach((k) => keys.push(k));
    });
    toggleKeys(keys);
  };

  const selectedCount = selectedKeys.size;

  const assignmentsToApply = useMemo(() => {
    return flattenAssignments(days, selectedKeys);
  }, [days, selectedKeys]);

  const activeDay = activeDate ? daysByDate[activeDate] : null;

  const todayStr = formatDefaultStartDate();
  const rangeIncludesTodayOrPast = startDate <= todayStr;

  const isMemberMoved = (member, slot) => {
    if (!member.originalDate) return false;
    return slot.date !== member.originalDate || slot.technicianId !== member.originalTechnicianId;
  };

  const openMoveForm = (slot, unit) => {
    setMoveForm({
      slotKey: `${slot.date}-${slot.technicianId}`,
      unitKey: unit.unitKey,
      unitLabel: unit.members.map((m) => m.displayName).join(' + '),
      fromDate: slot.date,
      fromTechnicianId: slot.technicianId,
      date: slot.date,
      technicianId: slot.technicianId,
    });
  };

  const performMove = async ({ fromDate, fromTechnicianId, unitKey, toDate, toTechnicianId }) => {
    if (fromDate === toDate && fromTechnicianId === toTechnicianId) {
      setMoveForm(null);
      return;
    }
    const fromDay = daysByDate[fromDate];
    const fromSlot = fromDay?.techSlots.find((s) => s.technicianId === fromTechnicianId);
    const unit = fromSlot?.assignments.find((u) => u.unitKey === unitKey);
    if (!unit) {
      toast.error('Could not find that visit to move');
      setMoveForm(null);
      return;
    }
    const toDay = daysByDate[toDate];
    const toSlot = toDay?.techSlots.find((s) => s.technicianId === toTechnicianId);

    const fromIds = fromSlot ? slotMemberKeysExcludingUnit(fromSlot, unitKey) : [];
    const toIds = [
      ...(toSlot ? slotMemberKeys(toSlot) : []),
      ...unit.members.map((m) => m.serviceCallId),
    ];

    setMoving(true);
    try {
      const [fromResponse, toResponse] = await Promise.all([
        fromSlot
          ? autoScheduleAPI.recomputeSlot({ date: fromDate, technicianId: fromTechnicianId, serviceCallIds: fromIds })
          : Promise.resolve(null),
        autoScheduleAPI.recomputeSlot({ date: toDate, technicianId: toTechnicianId, serviceCallIds: toIds }),
      ]);
      const updates = [];
      if (fromSlot) updates.push({ date: fromDate, technicianId: fromTechnicianId, slot: fromResponse.data.slot });
      updates.push({ date: toDate, technicianId: toTechnicianId, slot: toResponse.data.slot });
      setDays((prev) => applySlotUpdates(prev, updates));
      setActiveDate(toDate);
      setMoveForm(null);

      const toSlotResult = toResponse.data.slot;
      if (toSlotResult.incompatible) {
        toast.error(
          `${toSlotResult.technicianName} may not have the right capability for one of these visits`,
          { duration: 6000 }
        );
      } else if (toSlotResult.techRuleViolation) {
        toast(
          `Moved, but ${toSlotResult.technicianName} is blocked by this customer's technician rules`,
          { icon: '⚠️', duration: 6000 }
        );
      } else if (toSlotResult.overBudget) {
        toast(`Moved, but ${toSlotResult.technicianName} is now over budget on ${toDate}`, { icon: '⚠️' });
      } else {
        toast.success('Visit moved');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to move visit');
    } finally {
      setMoving(false);
    }
  };

  if (!isOpen) return null;

  // A thin connector row between two stops (or shop <-> stop) showing the
  // drive time between them.
  const renderConnector = (minutes, key) => (
    <div key={key} className="flex items-center gap-2 pl-[3px]">
      <div className="w-px h-4 bg-slate-200" />
      <span className="text-2xs text-slate-400 inline-flex items-center gap-1">
        <Navigation className="h-3 w-3" />
        {minutes != null ? `${fmtMin(minutes)} drive` : 'drive time unknown'}
      </span>
    </div>
  );

  const renderMoveForm = (slot, unit) => {
    if (!moveForm || moveForm.slotKey !== `${slot.date}-${slot.technicianId}` || moveForm.unitKey !== unit.unitKey) {
      return null;
    }
    const nextDay = formatLocalDate((() => {
      const d = parseLocalDate(slot.date);
      d.setDate(d.getDate() + 1);
      return d;
    })());
    return (
      <div className="mt-1.5 mb-1 p-2.5 rounded-md bg-slate-50 border border-slate-200 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={moveForm.technicianId}
            onChange={(e) => setMoveForm((f) => ({ ...f, technicianId: e.target.value }))}
            className="input text-xs py-1"
          >
            {reassignableTechnicians.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <input
            type="date"
            value={moveForm.date}
            onChange={(e) => setMoveForm((f) => ({ ...f, date: e.target.value }))}
            className="input text-xs py-1"
          />
          <button
            type="button"
            disabled={moving}
            onClick={() => performMove({
              fromDate: moveForm.fromDate,
              fromTechnicianId: moveForm.fromTechnicianId,
              unitKey: moveForm.unitKey,
              toDate: moveForm.date,
              toTechnicianId: moveForm.technicianId,
            })}
            className="btn btn-primary text-xs py-1 px-2.5 inline-flex items-center gap-1"
          >
            {moving ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRightLeft className="h-3 w-3" />}
            Move
          </button>
          <button
            type="button"
            disabled={moving}
            onClick={() => performMove({
              fromDate: moveForm.fromDate,
              fromTechnicianId: moveForm.fromTechnicianId,
              unitKey: moveForm.unitKey,
              toDate: nextDay,
              toTechnicianId: moveForm.fromTechnicianId,
            })}
            className="btn btn-secondary text-xs py-1 px-2.5 inline-flex items-center gap-1"
            title="Push this visit to the next day, same technician"
          >
            <CalendarClock className="h-3 w-3" />
            Push to next day
          </button>
          <button
            type="button"
            onClick={() => setMoveForm(null)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderSlotDetail = (slot) => {
    const color = techColorMap[slot.technicianId] || '#6b7280';
    const slotKeys = slotMemberKeys(slot);
    const allSelected = slotKeys.length > 0 && slotKeys.every((k) => selectedKeys.has(k));
    const existingMinutes = slot.existingMinutes || 0;
    const newMinutes = slot.newMinutes ?? (slot.usedMinutes - existingMinutes);
    const driveMinutes = slot.driveMinutes || 0;
    const overBy = slot.usedMinutes - slot.budgetMinutes;
    const shopConfigured = Boolean(preview?.settings?.shopConfigured);
    const shopAddress = preview?.shopAddress;
    return (
      <div key={`${slot.date}-${slot.technicianId}`} className="p-4">
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={() => toggleSlot(slot)}
            className="flex items-center gap-2 text-sm font-medium text-gray-800 hover:text-gray-900"
            title={allSelected ? 'Deselect all for this tech' : 'Select all for this tech'}
          >
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
            {slot.technicianName}
          </button>
          <div className="flex items-center gap-1.5">
            {slot.assignments.length > 0 && (
              <button
                type="button"
                onClick={() => setRouteSlot(slot)}
                className="text-2xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 hover:bg-blue-100 hover:text-blue-700 inline-flex items-center gap-0.5"
                title="View this technician's route on the map"
              >
                <Route className="h-3 w-3" />
                Route
              </button>
            )}
            {driveMinutes > 0 && (
              <span className="text-2xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 inline-flex items-center gap-0.5">
                <Navigation className="h-3 w-3" />
                {fmtMin(driveMinutes)} drive
              </span>
            )}
            <span className={clsx(
              'text-xs px-2 py-0.5 rounded-full',
              overBy > 0
                ? 'bg-red-100 text-red-800'
                : slot.usedMinutes >= slot.budgetMinutes
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-gray-100 text-gray-700'
            )}>
              {existingMinutes > 0
                ? `${fmtMin(existingMinutes)} existing + ${fmtMin(newMinutes)} new / ${fmtMin(slot.budgetMinutes)}`
                : `${fmtMin(slot.usedMinutes)} / ${fmtMin(slot.budgetMinutes)}`}
              {overBy > 0 && ` (over by ${fmtMin(overBy)})`}
            </span>
          </div>
        </div>

        <div className="space-y-0.5">
          {shopConfigured && (
            <>
              <div className="flex items-center gap-1.5 text-2xs text-slate-500 font-medium">
                <Building2 className="h-3.5 w-3.5" />
                Shop{shopAddress ? ` — ${shopAddress}` : ''}
              </div>
              {slot.assignments.length > 0 && renderConnector(slot.assignments[0].legTravelMinutes, 'connector-start')}
            </>
          )}
          {slot.assignments.map((unit, idx) => (
            <div key={unit.unitKey}>
              {idx > 0 && renderConnector(unit.legTravelMinutes, `connector-${unit.unitKey}`)}
              <div className={clsx(
                'text-sm rounded-md px-1.5 py-1',
                unit.incompatible && 'bg-red-50 ring-1 ring-red-200',
                !unit.incompatible && unit.techRuleViolation && 'bg-amber-50 ring-1 ring-amber-200'
              )}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center flex-wrap gap-1.5">
                    {unit.groupName && (
                      <span className="inline-block px-1.5 py-0.5 rounded text-2xs bg-purple-100 text-purple-800">
                        {unit.groupName}
                      </span>
                    )}
                    {unit.incompatible && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-2xs bg-red-100 text-red-700" title="This technician may not have the required capability">
                        <AlertTriangle className="h-3 w-3" />
                        capability mismatch
                      </span>
                    )}
                    {unit.techRuleViolation && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-2xs bg-amber-100 text-amber-800" title="This customer's technician rules exclude this tech — override in place">
                        <AlertTriangle className="h-3 w-3" />
                        tech rule override
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => (moveForm?.unitKey === unit.unitKey ? setMoveForm(null) : openMoveForm(slot, unit))}
                    className="text-2xs text-gray-400 hover:text-blue-600 inline-flex items-center gap-1 flex-shrink-0"
                    title="Reschedule this visit"
                  >
                    <ArrowRightLeft className="h-3 w-3" />
                    Move
                  </button>
                </div>
                {unit.members.map((member) => (
                  <label key={member.serviceCallId} className="flex items-center gap-2 py-0.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedKeys.has(member.serviceCallId)}
                      onChange={() => toggleMember(member.serviceCallId)}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    <span className="text-gray-900">{member.displayName}</span>
                    <span className={clsx(
                      'text-2xs px-1 rounded',
                      member.callKind === 'service' ? 'bg-blue-100 text-blue-800' : 'bg-teal-100 text-teal-800'
                    )}>
                      {member.callKind}
                    </span>
                    {member.overdue && (
                      <span className="text-2xs px-1 rounded bg-red-100 text-red-700 inline-flex items-center gap-0.5" title="Due in a past month">
                        <Clock className="h-2.5 w-2.5" />
                        overdue
                      </span>
                    )}
                    {!member.overdue && member.thisMonth && (
                      <span className="text-2xs px-1 rounded bg-amber-100 text-amber-700">this month</span>
                    )}
                    {isMemberMoved(member, slot) && (
                      <span
                        className="text-2xs px-1 rounded bg-violet-100 text-violet-800"
                        title="Re-scheduled from a previous assignment"
                      >
                        moved from {formatDayLabel(member.originalDate)}
                        {member.originalTechnicianName ? ` · ${member.originalTechnicianName}` : ''}
                      </span>
                    )}
                    <span className="text-2xs text-gray-500">{member.minutes}m</span>
                    {member.cityState && (
                      <span className="text-2xs text-gray-400 inline-flex items-center gap-0.5">
                        <MapPin className="h-3 w-3" />
                        {member.cityState}
                      </span>
                    )}
                  </label>
                ))}
              </div>
              {renderMoveForm(slot, unit)}
            </div>
          ))}
          {shopConfigured && preview?.settings?.returnToShop && slot.returnMinutes > 0 && (
            <>
              {renderConnector(slot.returnMinutes, 'connector-end')}
              <div className="flex items-center gap-1.5 text-2xs text-slate-500 font-medium">
                <Building2 className="h-3.5 w-3.5" />
                Shop (return)
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen px-4 py-6">
        <div className="fixed inset-0 bg-gray-900/50" onClick={onClose} />

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Auto-Schedule</h2>
              <p className="text-xs text-gray-500 mt-0.5">Contract service calls only — preview before applying</p>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-5 space-y-4 overflow-auto flex-1">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="input text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Optimization strategy</label>
                <select
                  value={strategy}
                  onChange={(e) => setStrategy(e.target.value)}
                  className="input text-sm min-w-[220px]"
                >
                  {STRATEGY_OPTIONS.map(({ id, label }) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => previewMutation.mutate()}
                disabled={previewMutation.isLoading || selectedTechIds?.size === 0}
                title={selectedTechIds?.size === 0 ? 'Select at least one technician in Advanced options' : undefined}
                className="btn btn-primary inline-flex items-center gap-2"
              >
                {previewMutation.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Calendar className="h-4 w-4" />
                )}
                Generate preview
              </button>

              <div ref={advancedRef}>
                <button
                  type="button"
                  onClick={() => setShowAdvanced((v) => !v)}
                  className={clsx(
                    'btn btn-secondary inline-flex items-center gap-1.5',
                    advancedFilterCount > 0 && 'ring-1 ring-blue-400'
                  )}
                  title="Advanced options"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  Advanced
                  {advancedFilterCount > 0 && (
                    <span className="inline-flex items-center justify-center h-4 w-4 text-2xs rounded-full bg-blue-600 text-white">
                      {advancedFilterCount}
                    </span>
                  )}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </div>

              {preview && (
                <div className="ml-auto inline-flex rounded-md border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setViewMode('calendar')}
                    className={clsx(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm',
                      viewMode === 'calendar' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    <CalendarDays className="h-4 w-4" />
                    Calendar
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('list')}
                    className={clsx(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border-l border-gray-300',
                      viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    <List className="h-4 w-4" />
                    List
                  </button>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-gray-800">Compare strategies</p>
                  <p className="text-2xs text-gray-500">Pick 2–4 strategies, then compare side-by-side before loading a plan.</p>
                </div>
                <button
                  type="button"
                  onClick={() => compareMutation.mutate()}
                  disabled={
                    compareMutation.isLoading
                    || compareStrategyIds.size < 2
                    || selectedTechIds?.size === 0
                  }
                  className="btn btn-secondary inline-flex items-center gap-2 text-sm"
                >
                  {compareMutation.isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Route className="h-4 w-4" />
                  )}
                  Compare strategies
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {STRATEGY_OPTIONS.map(({ id, label }) => (
                  <label
                    key={id}
                    className={clsx(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs cursor-pointer',
                      compareStrategyIds.has(id)
                        ? 'bg-blue-50 border-blue-300 text-blue-800'
                        : 'bg-white border-gray-300 text-gray-700'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={compareStrategyIds.has(id)}
                      onChange={() => toggleCompareStrategy(id)}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {compareResults && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">Strategy comparison</h3>
                  <button
                    type="button"
                    onClick={() => setCompareResults(null)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Dismiss
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {compareResults.options.map((option) => (
                    <div
                      key={option.strategy}
                      className="rounded-lg border border-gray-200 bg-white p-4 space-y-3"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{option.strategyLabel}</p>
                        <p className="text-2xs text-gray-500 mt-0.5">{option.strategy}</p>
                      </div>
                      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                        <div>
                          <dt className="text-gray-500">Total drive</dt>
                          <dd className="font-semibold text-gray-900">{fmtMin(option.metrics.totalDriveMinutes)}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Stops placed</dt>
                          <dd className="font-semibold text-gray-900">{option.metrics.totalStops}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Unplaced</dt>
                          <dd className="font-semibold text-gray-900">{option.preview.summary.unplacedUnits}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Avg stops / tech-day</dt>
                          <dd className="font-semibold text-gray-900">{option.metrics.avgStopsPerTechDay}</dd>
                        </div>
                      </dl>
                      <button
                        type="button"
                        onClick={() => {
                          setStrategy(option.strategy);
                          loadPreview(option.preview);
                          toast.success(`Loaded ${option.strategyLabel} plan`);
                        }}
                        className="btn btn-primary w-full text-sm"
                      >
                        Use this plan
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <label className="flex items-start gap-2 text-sm cursor-pointer max-w-xl">
              <input
                type="checkbox"
                checked={includeScheduled}
                onChange={(e) => setIncludeScheduled(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-blue-600"
              />
              <span>
                <span className="text-gray-800 font-medium">Include already-scheduled visits (re-schedule)</span>
                <span className="block text-2xs text-gray-500">
                  Reshuffles calendar visits in this range, except locked ones. Also fills new backlog work.
                </span>
              </span>
            </label>

            {includeScheduled && rangeIncludesTodayOrPast && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  This range includes today or past dates — some technicians may already be en route.
                  Review moved visits carefully before applying.
                </span>
              </div>
            )}

            {geocodeCount > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  {geocodeCount} active client(s) missing coordinates — they cannot be placed until geocoded.
                  Run geocoding from Database Tools in Settings.
                </span>
              </div>
            )}

            {preview && preview.travelSource === 'haversine' && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-slate-50 border border-slate-200 text-sm text-slate-700">
                <Navigation className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Using straight-line distance estimates ({preview.osrmConfigured ? 'routing service unavailable' : 'road routing not configured'})
                  {preview.settings?.avgSpeedMph ? ` at ~${preview.settings.avgSpeedMph} mph` : ''}.
                  {!preview.settings?.shopConfigured && ' No shop address set — routes seed from the farthest client.'}
                </span>
              </div>
            )}

            {preview && (
              <>
                {preview.strategyLabel && (
                  <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs font-medium">
                      <Route className="h-3.5 w-3.5" />
                      {preview.strategyLabel}
                    </span>
                    {preview.summary.totalDriveMinutes != null && (
                      <span className="text-xs text-gray-500">
                        Fleet drive time: {fmtMin(preview.summary.totalDriveMinutes)}
                        {preview.summary.avgStopsPerTechDay != null && (
                          <> · avg {preview.summary.avgStopsPerTechDay} stops/tech-day</>
                        )}
                      </span>
                    )}
                  </div>
                )}

                <div className={clsx(
                  'grid gap-3 text-sm',
                  preview.filters?.includeScheduled
                    ? 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-7'
                    : 'grid-cols-2 sm:grid-cols-4'
                )}>
                  <div className="p-3 rounded-md bg-gray-50 border border-gray-200">
                    <div className="text-xs text-gray-500">Eligible calls</div>
                    <div className="font-semibold">{preview.summary.eligibleCalls}</div>
                    {(preview.summary.overdueCalls > 0 || preview.summary.thisMonthCalls > 0) && (
                      <div className="text-2xs text-gray-400 mt-0.5">
                        {preview.summary.overdueCalls > 0 && `${preview.summary.overdueCalls} overdue`}
                        {preview.summary.overdueCalls > 0 && preview.summary.thisMonthCalls > 0 && ' · '}
                        {preview.summary.thisMonthCalls > 0 && `${preview.summary.thisMonthCalls} this month`}
                      </div>
                    )}
                  </div>
                  {preview.filters?.includeScheduled && (
                    <>
                      <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                        <div className="text-xs text-slate-600">Locked (excluded)</div>
                        <div className="font-semibold text-slate-900">{preview.summary.lockedCalls ?? 0}</div>
                      </div>
                      <div className="p-3 rounded-md bg-violet-50 border border-violet-200">
                        <div className="text-xs text-violet-700">Moved</div>
                        <div className="font-semibold text-violet-900">{preview.summary.movedCalls ?? 0}</div>
                      </div>
                      <div className="p-3 rounded-md bg-teal-50 border border-teal-200">
                        <div className="text-xs text-teal-700">Unchanged</div>
                        <div className="font-semibold text-teal-900">{preview.summary.unchangedCalls ?? 0}</div>
                      </div>
                    </>
                  )}
                  <div className="p-3 rounded-md bg-green-50 border border-green-200">
                    <div className="text-xs text-green-700">Placed stops</div>
                    <div className="font-semibold text-green-900">{preview.summary.placedUnits}</div>
                  </div>
                  <div className="p-3 rounded-md bg-red-50 border border-red-200">
                    <div className="text-xs text-red-700">Unplaced</div>
                    <div className="font-semibold text-red-900">{preview.summary.unplacedUnits}</div>
                  </div>
                  <div className="p-3 rounded-md bg-blue-50 border border-blue-200">
                    <div className="text-xs text-blue-700">Selected to apply</div>
                    <div className="font-semibold text-blue-900">{selectedCount}</div>
                  </div>
                </div>

                {technicians.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-gray-500 mr-1">Technicians:</span>
                    <button
                      type="button"
                      onClick={() => setTechFilter(null)}
                      className={clsx(
                        'text-xs px-2.5 py-1 rounded-full border transition-colors',
                        techFilter === null
                          ? 'bg-gray-800 text-white border-gray-800'
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      )}
                    >
                      All
                    </button>
                    {technicians.map((tech) => {
                      const active = techFilter === tech.id;
                      return (
                        <button
                          key={tech.id}
                          type="button"
                          onClick={() => setTechFilter(active ? null : tech.id)}
                          className="text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 transition-colors"
                          style={{
                            backgroundColor: active ? tech.color : `${tech.color}18`,
                            borderColor: tech.color,
                            color: active ? '#fff' : tech.color,
                          }}
                        >
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: active ? '#fff' : tech.color }}
                          />
                          {tech.name}
                        </button>
                      );
                    })}
                  </div>
                )}

                {days.length === 0 ? (
                  <div className="text-sm text-gray-500 py-6 text-center">No assignments in preview for this range.</div>
                ) : viewMode === 'calendar' ? (
                  <div className="space-y-4">
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
                        {WEEKDAY_LABELS.map((label) => (
                          <div key={label} className="px-2 py-1.5 text-2xs font-semibold text-gray-500 uppercase tracking-wide text-center">
                            {label}
                          </div>
                        ))}
                      </div>
                      <div>
                        {calendarWeeks.map((week) => (
                          <div key={week[0]} className="grid grid-cols-7 border-b border-gray-100 last:border-b-0">
                            {week.map((dateStr) => {
                              const day = daysByDate[dateStr];
                              const inRange = Boolean(day) || (dateStr >= preview.startDate && dateStr <= preview.endDate);
                              const slots = day ? visibleSlots(day) : [];
                              const isActive = dateStr === activeDate;
                              const dayNum = parseLocalDate(dateStr).getDate();
                              return (
                                <button
                                  key={dateStr}
                                  type="button"
                                  disabled={!day}
                                  onClick={() => day && setActiveDate(dateStr)}
                                  className={clsx(
                                    'min-h-[92px] border-r border-gray-100 last:border-r-0 p-1.5 text-left align-top transition-colors',
                                    !inRange && 'bg-gray-50/60',
                                    day ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-default',
                                    isActive && 'ring-2 ring-inset ring-blue-500 bg-blue-50'
                                  )}
                                >
                                  <div className={clsx(
                                    'text-2xs font-semibold mb-1',
                                    inRange ? 'text-gray-600' : 'text-gray-300'
                                  )}>
                                    {dayNum}
                                  </div>
                                  <div className="space-y-1">
                                    {slots.map((slot) => {
                                      const color = techColorMap[slot.technicianId] || '#6b7280';
                                      const stops = slot.assignments.reduce(
                                        (sum, u) => sum + u.members.length,
                                        0
                                      );
                                      const existingMinutes = slot.existingMinutes || 0;
                                      const title = existingMinutes > 0
                                        ? `${slot.technicianName} — ${stops} new stop(s); ${fmtMin(existingMinutes)} existing + ${fmtMin(slot.newMinutes ?? (slot.usedMinutes - existingMinutes))} new / ${fmtMin(slot.budgetMinutes)}${slot.driveMinutes ? `; ${fmtMin(slot.driveMinutes)} drive` : ''}`
                                        : `${slot.technicianName} — ${stops} stop(s), ${fmtMin(slot.usedMinutes)}/${fmtMin(slot.budgetMinutes)}${slot.driveMinutes ? `, ${fmtMin(slot.driveMinutes)} drive` : ''}`;
                                      return (
                                        <div
                                          key={slot.technicianId}
                                          className="rounded px-1 py-0.5 text-2xs leading-tight truncate flex items-center gap-1"
                                          style={{ backgroundColor: `${color}1f`, color }}
                                          title={title}
                                        >
                                          {existingMinutes > 0 && (
                                            <span
                                              className="inline-block h-1.5 w-1.5 rounded-full flex-shrink-0 ring-1 ring-white"
                                              style={{ backgroundColor: color }}
                                              aria-label="Has existing work"
                                            />
                                          )}
                                          <span className="font-medium truncate">{slot.technicianName}</span>
                                          <span className="flex-shrink-0">{' · '}{stops}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>

                    {activeDay ? (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                          <span className="font-medium text-sm text-gray-900">{formatDayLabel(activeDay.date)}</span>
                          <button
                            type="button"
                            onClick={() => toggleDay(activeDay)}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Toggle all {techFilter ? 'shown ' : ''}visits
                          </button>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {visibleSlots(activeDay).length === 0 ? (
                            <div className="p-4 text-sm text-gray-500">No assignments for the selected technician on this day.</div>
                          ) : (
                            visibleSlots(activeDay).map((slot) => renderSlotDetail(slot))
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 text-center py-4">
                        Select a day on the calendar to review and adjust its visits.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {days.map((day) => {
                      const slots = visibleSlots(day);
                      if (slots.length === 0) return null;
                      return (
                        <div key={day.date} className="border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggleDay(day)}
                            className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 hover:bg-gray-100 text-left"
                          >
                            <span className="font-medium text-sm text-gray-900">{formatDayLabel(day.date)}</span>
                            <span className="text-xs text-gray-500">{slots.length} tech slot(s)</span>
                          </button>
                          <div className="divide-y divide-gray-100">
                            {slots.map((slot) => renderSlotDetail(slot))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {preview.unplaceable?.length > 0 && (
                  <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <h3 className="text-sm font-semibold text-red-900 mb-2">Could not place</h3>
                    <ul className="space-y-1 text-sm text-red-800">
                      {preview.unplaceable.map((unit) => (
                        <li key={unit.unitKey}>
                          {unit.members.map((m) => m.displayName).join(' + ')}
                          {' — '}
                          <span className="text-red-600">{REASON_LABELS[unit.reason] || unit.reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => applyMutation.mutate(assignmentsToApply)}
              disabled={!preview || selectedCount === 0 || applyMutation.isLoading}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              {applyMutation.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              Apply {selectedCount} assignment(s)
            </button>
          </div>
        </div>
      </div>

      {showAdvanced && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex items-start justify-center min-h-screen px-4 py-6">
            <div className="fixed inset-0 bg-gray-900/50" onClick={() => setShowAdvanced(false)} />

            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Advanced options</h2>
                <button type="button" onClick={() => setShowAdvanced(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-gray-700">Technicians to include</span>
                    <button
                      type="button"
                      onClick={() => setSelectedTechIds(
                        allTechIdsSelected ? new Set() : new Set(allTechnicians.map((t) => t.id))
                      )}
                      className="text-2xs text-blue-600 hover:text-blue-800"
                    >
                      {allTechIdsSelected ? 'Clear all' : 'Select all'}
                    </button>
                  </div>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
                    {allTechnicians.length === 0 ? (
                      <div className="p-2 text-xs text-gray-400">No active technicians</div>
                    ) : (
                      allTechnicians.map((t) => (
                        <label key={t.id} className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={Boolean(selectedTechIds?.has(t.id))}
                            onChange={() => toggleTechSelection(t.id)}
                            className="rounded border-gray-300 text-blue-600"
                          />
                          <span className="text-gray-800">{t.firstName} {t.lastName}</span>
                          {!t.includeInAutoSchedule && (
                            <span className="text-2xs text-gray-400 ml-auto">not default</span>
                          )}
                        </label>
                      ))
                    )}
                  </div>
                  {selectedTechIds?.size === 0 && (
                    <p className="text-2xs text-red-600 mt-1">Select at least one technician.</p>
                  )}
                </div>

                <div className="space-y-2 border-t border-gray-100 pt-3">
                  <span className="text-xs font-semibold text-gray-700 block">Prioritization</span>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={prioritizeOverdue}
                      onChange={(e) => setPrioritizeOverdue(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-blue-600"
                    />
                    <span>
                      <span className="text-gray-800">Prioritize overdue calls</span>
                      <span className="block text-2xs text-gray-500">Sweep in unassigned calls from past months and schedule them first</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={prioritizeThisMonth}
                      onChange={(e) => setPrioritizeThisMonth(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-blue-600"
                    />
                    <span>
                      <span className="text-gray-800">Prioritize this month</span>
                      <span className="block text-2xs text-gray-500">Favor calls due this month over those due later in the range</span>
                    </span>
                  </label>
                </div>

                <div className="space-y-2 border-t border-gray-100 pt-3">
                  <span className="text-xs font-semibold text-gray-700 block">Call type</span>
                  <select
                    value={callKindFilter}
                    onChange={(e) => setCallKindFilter(e.target.value)}
                    className="input text-sm w-full"
                  >
                    <option value="all">All (service + inspection)</option>
                    <option value="service">Service only</option>
                    <option value="inspection">Inspection only</option>
                  </select>
                </div>

                <div className="space-y-1.5 border-t border-gray-100 pt-3">
                  <span className="text-xs font-semibold text-gray-700 block">Max stops per tech / day</span>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    placeholder="No limit"
                    value={maxStopsPerTech}
                    onChange={(e) => setMaxStopsPerTech(e.target.value)}
                    className="input text-sm w-full"
                  />
                </div>
              </div>

              <div className="px-5 py-4 border-t border-gray-200 flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedTechIds(new Set(allTechnicians.filter((t) => t.includeInAutoSchedule).map((t) => t.id)));
                    setPrioritizeOverdue(false);
                    setPrioritizeThisMonth(false);
                    setCallKindFilter('all');
                    setMaxStopsPerTech('');
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Reset to defaults
                </button>
                <button type="button" onClick={() => setShowAdvanced(false)} className="btn btn-primary">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {routeSlot && (
        <TechRouteMapModal
          slot={routeSlot}
          preview={preview}
          color={techColorMap[routeSlot.technicianId]}
          onClose={() => setRouteSlot(null)}
        />
      )}
    </div>
  );
}
